import React from 'react';
import { connect } from 'react-redux';
import AdminShell, { Icon } from './AdminShell';
import { Modal } from './Overlay';
import { Switch, Field } from './Form';

/*
 * Roles & permissions — layar `roles` paket client.
 *
 * MATRIKS, BUKAN DAFTAR. Bentuknya diambil apa adanya dari prototipe: hak
 * akses menurun, peran melintang, satu saklar per sel, dan palang perubahan
 * yang muncul begitu ada yang digeser. Yang berbeda cuma satu: kalimat
 * pembuka prototipe berbunyi "Enforced on the server, not just hidden in the
 * menu" sementara spanduk merahnya sendiri mengatakan sebaliknya. Yang benar
 * adalah spanduknya, dan itu yang dipakai.
 *
 * APA YANG SEBENARNYA DILAKUKAN HAK AKSES. Token admin berisi id, email dan
 * `admin: true` — tidak ada peran di dalamnya. Setiap resolver hanya bertanya
 * "apakah kamu admin". Satu-satunya tempat hak akses dibaca adalah `action()`
 * berkas route dashboard LAMA, lewat `restrictUrls`, yang mencocokkan alamat
 * yang ditulis tetap di berkas itu dengan tabel PrivilegesLink. Jadi mematikan
 * saklar menyembunyikan halaman; ia tidak menghentikan satu pun tindakan.
 *
 * Karena itu tiap baris menyebut berapa halaman yang dibukanya, dan layar ini
 * tidak memakai kata "permission" tanpa mengatakan batasnya.
 *
 * MENYIMPAN MENGGANTI SELURUH DAFTAR sebuah peran. createAdminRole menghapus
 * semua baris hak akses peran itu lalu menulis ulang apa yang dikirim, jadi
 * yang dikirim selalu daftar lengkap kolom itu — bukan cuma yang berubah.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query {
  getAdminRoleMatrix {
    status errorMessage
    roles { id name description privileges adminCount }
    privileges { id name pageCount pages }
  }
}`;

const M_SAVE = `mutation ($id: Int, $name: String!, $description: String, $privileges: [Int]!) {
  createAdminRole(id: $id, name: $name, description: $description, privileges: $privileges) {
    status errorMessage
  }
}`;

const M_DELETE = `mutation ($id: Int!) {
  deleteAdminRole(id: $id) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const PRIV_AR = {
  'Manage Site and Mobile App Settings': 'إدارة إعدادات الموقع وتطبيق الجوال',
  'Manage Homepage Settings': 'إدارة إعدادات الصفحة الرئيسية',
  'Manage Users': 'إدارة العملاء',
  'Manage Service Providers': 'إدارة مقدمي الخدمة',
  'Manage Categories': 'إدارة الخدمات',
  'Manage SubCategories': 'إدارة الخدمات الفرعية',
  'Manage Location': 'إدارة المواقع',
  'Manage Fare': 'إدارة التسعير',
  'Manage Jobs': 'إدارة الطلبات',
  'Manage Ratings': 'إدارة التقييمات',
  'Manage Promo Code': 'إدارة الرموز الترويجية',
  'Manage Notifications': 'إدارة الإشعارات',
  'Cancel Reason': 'إدارة أسباب الإلغاء',
  'Manage Payout': 'إدارة الدفعات',
  'Manage Static Content': 'إدارة المحتوى الثابت',
  'Manage CMS Pages': 'إدارة صفحات المحتوى',
  'Precaution Notification': 'إشعارات التنبيه',
  'Manage SMS Methods': 'إدارة موفري الرسائل النصية',
  'Manage Tracking': 'إدارة التتبع',
  'Manage Payment GateWay': 'إدارة بوابة الدفع'
};

class Roles extends React.Component {
  state = {
    roles: [], privileges: [], grants: {}, original: {},
    loading: true, error: null, notice: null, busy: false,
    editing: null, removing: null, tried: false,
    form: { name: '', description: '', copyFrom: '' }
  };

  componentDidMount() { this.load(); }

  isArabic = () => String(this.props.currentLocale || '').startsWith('ar');
  tx = (en, ar) => this.isArabic() ? ar : en;
  privilegeName = name => this.isArabic() ? (PRIV_AR[name] || name) : name;

  errorText = (message, en, ar) => {
    if (!this.isArabic()) return message || en;
    const value = String(message || '').toLowerCase();
    if (value.includes('super admin')) return 'يمكن للمسؤول الأعلى فقط إدارة الأدوار والصلاحيات.';
    if (value.includes('already has')) return 'يوجد دور آخر بهذا الاسم.';
    if (value.includes('no longer exist')) return 'لم تعد إحدى الصلاحيات المحددة موجودة.';
    if (value.includes('using this role')) return 'لا يمكن حذف دور تستخدمه حسابات إدارية.';
    return ar;
  };

  load = async () => {
    this.setState({ loading: true, error: null });
    const json = await gql(Q, {}).catch(() => null);
    const res = json && json.data && json.data.getAdminRoleMatrix;
    if (!ok(res)) {
      this.setState({ loading: false, error: this.errorText(res && res.errorMessage,
        'Could not load the roles.', 'تعذّر تحميل الأدوار والصلاحيات.') });
      return;
    }
    // grants[roleId] = Set-seperti objek { privilegeId: true }
    const grants = {};
    (res.roles || []).forEach(r => {
      grants[r.id] = {};
      (r.privileges || []).forEach(p => { grants[r.id][p] = true; });
    });
    this.setState({
      roles: res.roles || [],
      privileges: res.privileges || [],
      grants,
      original: JSON.parse(JSON.stringify(grants)),
      loading: false
    });
  };

  toggle = (roleId, privId, on) => this.setState(st => ({
    grants: {
      ...st.grants,
      [roleId]: { ...st.grants[roleId], [privId]: on }
    },
    notice: null
  }));

  // Perubahan yang belum disimpan, per peran.
  changed() {
    const { roles, privileges, grants, original } = this.state;
    const out = [];
    roles.forEach(r => {
      const diff = privileges.filter(p =>
        !!(grants[r.id] && grants[r.id][p.id]) !== !!(original[r.id] && original[r.id][p.id]));
      if (diff.length) out.push({ role: r, diff });
    });
    return out;
  }

  discard = () => this.setState(st => ({
    grants: JSON.parse(JSON.stringify(st.original)), notice: null
  }));

  saveMatrix = async () => {
    const pending = this.changed();
    if (!pending.length) return;
    const { grants } = this.state;
    this.setState({ busy: true });

    let failed = null;
    for (let i = 0; i < pending.length; i += 1) {
      const r = pending[i].role;
      // Daftar LENGKAP, bukan selisihnya — mutation-nya mengganti, bukan menambah.
      const list = this.state.privileges
        .filter(p => grants[r.id] && grants[r.id][p.id])
        .map(p => p.id);
      // eslint-disable-next-line no-await-in-loop
      const json = await gql(M_SAVE, {
        id: r.id, name: r.name, description: r.description, privileges: list
      }).catch(() => null);
      const res = json && json.data && json.data.createAdminRole;
      if (!ok(res)) { failed = { role: r, msg: this.errorText(res && res.errorMessage,
        'save failed', 'فشل الحفظ') }; break; }
    }

    this.setState({ busy: false });
    await this.load();
    this.setState({
      notice: failed
        ? { tone: 'danger', text: this.tx(
          `Could not save ${failed.role.name}: ${failed.msg}. Nothing after it was saved either.`,
          `تعذّر حفظ ${failed.role.name}: ${failed.msg}. ولم تُحفظ التغييرات التالية.`) }
        : { tone: 'info', text: this.tx(
          'Permissions saved. They change which pages the classic dashboard offers on the next request.',
          'حُفظت الصلاحيات. ستغيّر الصفحات المتاحة للدور في لوحة التحكم الكلاسيكية عند الطلب التالي.') }
    });
  };

  openNew = () => this.setState({
    editing: { id: null }, form: { name: '', description: '', copyFrom: '' },
    tried: false, notice: null
  });

  openEdit = r => this.setState({
    editing: r,
    form: { name: r.name || '', description: r.description || '', copyFrom: '' },
    tried: false, notice: null
  });

  formError() {
    const name = String(this.state.form.name || '').trim();
    if (!name) return this.tx('A role needs a name.', 'اسم الدور مطلوب.');
    const clash = this.state.roles.some(r =>
      r.name.toLowerCase() === name.toLowerCase()
      && (!this.state.editing.id || r.id !== this.state.editing.id));
    if (clash) return this.tx('Another role already has that name.', 'يوجد دور آخر بهذا الاسم.');
    return null;
  }

  saveRole = async () => {
    if (this.formError()) { this.setState({ tried: true }); return; }
    const { editing, form, grants } = this.state;

    /*
     * Peran baru mewarisi hak akses peran yang dipilih. Tanpa ini, peran baru
     * lahir tanpa satu pun hak akses dan harus diklik dua puluh kali sebelum
     * berguna.
     */
    let list = [];
    if (editing.id) {
      list = this.state.privileges
        .filter(p => grants[editing.id] && grants[editing.id][p.id]).map(p => p.id);
    } else if (form.copyFrom) {
      const src = Number(form.copyFrom);
      list = this.state.privileges
        .filter(p => grants[src] && grants[src][p.id]).map(p => p.id);
    }

    this.setState({ busy: true });
    const json = await gql(M_SAVE, {
      id: editing.id || null,
      name: String(form.name).trim(),
      description: String(form.description || '').trim(),
      privileges: list
    }).catch(() => null);
    const res = json && json.data && json.data.createAdminRole;
    this.setState({ busy: false });

    if (!ok(res)) {
      this.setState({ notice: { tone: 'danger', text: this.errorText(res && res.errorMessage,
        'Could not save the role.', 'تعذّر حفظ الدور.') } });
      return;
    }
    this.setState({ editing: null });
    await this.load();
    this.setState({ notice: { tone: 'info', text: editing.id
      ? this.tx('Role updated.', 'تم تحديث الدور.')
      : this.tx('Role created.', 'تم إنشاء الدور.') } });
  };

  removeRole = async () => {
    const { removing } = this.state;
    this.setState({ busy: true });
    const json = await gql(M_DELETE, { id: removing.id }).catch(() => null);
    const res = json && json.data && json.data.deleteAdminRole;
    this.setState({ busy: false, removing: null });

    if (!ok(res)) {
      this.setState({ notice: { tone: 'danger', text: this.errorText(res && res.errorMessage,
        'Could not delete the role.', 'تعذّر حذف الدور.') } });
      return;
    }
    await this.load();
    this.setState({ notice: { tone: 'info', text: this.tx(
      `Role “${removing.name}” deleted.`, `تم حذف الدور «${removing.name}».`) } });
  };

  render() {
    const {
      roles, privileges, grants, loading, error, notice, busy,
      editing, removing, tried, form
    } = this.state;
    const pending = this.changed();
    const pendingCount = pending.reduce((n, p) => n + p.diff.length, 0);

    return (
      <AdminShell
        current="roles"
        breadcrumb={[this.tx('System', 'النظام'), this.tx('Roles & permissions', 'الأدوار والصلاحيات')]}
        title={this.tx('Roles & permissions', 'الأدوار والصلاحيات')}
        lead={this.tx('Which pages each role is offered in the classic dashboard.',
          'حدّد الصفحات المتاحة لكل دور في لوحة التحكم الكلاسيكية.')}
        actions={[
          <button className="btn" type="button" key="n" onClick={this.openNew}>
            <Icon name="plus" />{this.tx('Add role', 'إضافة دور')}
          </button>,
          <a className="btn ghost" href="/siteadmin/admin-roles" key="c">
            {this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}
          </a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{this.tx('Dismiss', 'إغلاق')}</button>
          </div>
        )}

        {/* Spanduk prototipe sendiri, dan ia benar. */}
        <div className="notice danger">
          <span className="ni">!</span>
          <span className="nt">
            {this.tx(
              'Most permissions are enforced in the classic routing layer only. Turning a switch off hides its pages, but most underlying actions still need server-side permission checks. Newer high-risk payout actions are checked on the server.',
              'تُطبّق معظم الصلاحيات في مسارات لوحة التحكم الكلاسيكية فقط. إيقاف الصلاحية يخفي صفحاتها، لكن معظم الإجراءات الأساسية ما زالت تحتاج إلى تحقق على الخادم. تُفحص إجراءات الدفعات الحديثة عالية الخطورة على الخادم.')}
          </span>
        </div>

        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {this.tx(
              'These switches currently control the classic dashboard. New dashboard pages do not have privilege entries yet, so authenticated admins can open them independently of this matrix.',
              'تتحكم هذه المفاتيح حالياً في لوحة التحكم الكلاسيكية. صفحات لوحة التحكم الجديدة لا تملك سجلات صلاحيات بعد، لذلك يمكن للمسؤولين المسجلين فتحها بشكل مستقل عن هذه المصفوفة.')}
          </span>
        </div>

        {error && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{error}</span>
          </div>
        )}

        {loading
          ? <section className="card"><div className="card-b">{this.tx('Loading…', 'جارٍ التحميل…')}</div></section>
          : (
            <section className="card">
              <div className="card-h">
                <h2>{this.tx('Permission matrix', 'مصفوفة الصلاحيات')}</h2>
                <span className="sp" />
                <span className="tag"><i />{this.tx(
                  `${roles.length} role${roles.length === 1 ? '' : 's'}`, `${roles.length} دور`)}</span>
              </div>

              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{this.tx('Permission', 'الصلاحية')}</th>
                      {roles.map(r => (
                        <th key={r.id}>
                          {r.name}
                          {/* `.sub` cuma bergaya di dalam `.who`; di sini
                              pakai `.muted` yang memang global. */}
                          <span className="muted" style={{ display: 'block', fontWeight: 400 }}>
                            {this.tx(`${r.adminCount} admin${r.adminCount === 1 ? '' : 's'}`,
                              `${r.adminCount} مسؤول`)}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {privileges.map(p => (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{this.privilegeName(p.name)}</div>
                          <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}
                            title={(p.pages || []).join('\n')}>
                            {p.pageCount === 0
                              ? this.tx('Opens no page — nothing is wired to it', 'لا تفتح أي صفحة — غير مرتبطة حالياً')
                              : this.tx(`Opens ${p.pageCount} page${p.pageCount === 1 ? '' : 's'}`,
                                `تفتح ${p.pageCount} صفحة`)}
                          </div>
                        </td>
                        {roles.map(r => (
                          <td key={r.id}>
                            {/* `.switch` diberi lebar dan tinggi tapi berupa
                                <label>, jadi di dalam sel tabel ia inline dan
                                ukurannya diabaikan — saklarnya mengecil jadi
                                satu titik. Aturan kliennya sendiri menulis
                                `flex:none`, artinya memang diniatkan sebagai
                                anak flex. */}
                            <span style={{ display: 'flex' }}>
                              <Switch
                                checked={!!(grants[r.id] && grants[r.id][p.id])}
                                label={this.tx(`${p.name} for ${r.name}`, `${this.privilegeName(p.name)} للدور ${r.name}`)}
                                onChange={on => this.toggle(r.id, p.id, on)}
                              />
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Kaki: kelola perannya sendiri, bukan selnya. */}
              <div className="card-b" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {roles.map(r => (
                  <span key={r.id} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <button className="btn ghost sm" type="button" onClick={() => this.openEdit(r)}>
                      <Icon name="edit" />{r.name}
                    </button>
                    <button className="btn icon" type="button"
                      title={r.adminCount > 0
                        ? this.tx(`${r.adminCount} admin account${r.adminCount === 1 ? ' uses' : 's use'} this role`,
                          `${r.adminCount} حساب إداري يستخدم هذا الدور`)
                        : this.tx(`Delete ${r.name}`, `حذف ${r.name}`)}
                      aria-label={this.tx(`Delete ${r.name}`, `حذف ${r.name}`)}
                      disabled={r.adminCount > 0}
                      onClick={() => this.setState({ removing: r, notice: null })}>
                      <Icon name="trash" />
                    </button>
                  </span>
                ))}
              </div>
            </section>
          )}

        {/* Palang perubahan — sama seperti `dirty` di prototipe. */}
        {pendingCount > 0 && (
          <div className="dirty">
            <div className="dm">
              <b>{this.tx(`${pendingCount} unsaved change${pendingCount === 1 ? '' : 's'}`,
                `${pendingCount} تغيير غير محفوظ`)}</b>
              <span>{this.tx(
                ` — across ${pending.length} role${pending.length === 1 ? '' : 's'}. Nothing is written until you save.`,
                ` — في ${pending.length} دور. لن يُكتب شيء حتى تحفظ.`)}</span>
            </div>
            <button className="btn ghost" type="button" onClick={this.discard} disabled={busy}>
              {this.tx('Discard', 'تجاهل')}
            </button>
            <button className="btn" type="button" onClick={this.saveMatrix} disabled={busy}>
              <Icon name="check" />{busy ? this.tx('Saving…', 'جارٍ الحفظ…') : this.tx('Save permissions', 'حفظ الصلاحيات')}
            </button>
          </div>
        )}

        {editing && (
          <Modal
            title={editing.id
              ? this.tx(`Edit ${editing.name}`, `تعديل ${editing.name}`)
              : this.tx('Add a role', 'إضافة دور')}
            sub={editing.id
              ? this.tx('Permissions are set in the matrix, not here.', 'تُضبط الصلاحيات في المصفوفة، وليس هنا.')
              : this.tx('Set its permissions in the matrix once it exists.', 'اضبط صلاحياته في المصفوفة بعد إنشائه.')}
            icon="key"
            confirmLabel={editing.id ? this.tx('Save role', 'حفظ الدور') : this.tx('Create role', 'إنشاء الدور')}
            busy={busy}
            onConfirm={this.saveRole}
            onClose={() => this.setState({ editing: null })}
          >
            <div className="form-grid">
              <Field label={this.tx('Role name', 'اسم الدور')} required span>
                <input className="inp" value={form.name} placeholder={this.tx('Night dispatcher', 'موزّع المناوبة الليلية')}
                  onChange={e => { const v = e.target.value; this.setState(st => ({ form: { ...st.form, name: v } })); }} />
              </Field>
              <Field label={this.tx('Description', 'الوصف')} span
                hint={this.tx('Only shown on this screen. Nothing reads it.', 'يظهر في هذه الشاشة فقط ولا يؤثر في النظام.')}>
                <input className="inp" value={form.description}
                  onChange={e => { const v = e.target.value; this.setState(st => ({ form: { ...st.form, description: v } })); }} />
              </Field>
              {!editing.id && (
                <Field label={this.tx('Start from', 'البدء من')} span
                  hint={this.tx("Copies that role's permissions as a starting point. You can change them after.",
                    'ينسخ صلاحيات الدور كنقطة بداية، ويمكنك تعديلها لاحقاً.')}>
                  <select className="inp" value={form.copyFrom}
                    onChange={e => { const v = e.target.value; this.setState(st => ({ form: { ...st.form, copyFrom: v } })); }}>
                    <option value="">{this.tx('No permissions', 'بلا صلاحيات')}</option>
                    {roles.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                  </select>
                </Field>
              )}
            </div>
            {tried && this.formError() && (
              <div className="notice danger" style={{ marginTop: 12 }}>
                <span className="ni">!</span><span className="nt">{this.formError()}</span>
              </div>
            )}
          </Modal>
        )}

        {removing && (
          <Modal
            title={this.tx(`Delete “${removing.name}”?`, `حذف «${removing.name}»؟`)}
            sub={this.tx('Its permissions go with it.', 'ستُحذف صلاحياته معه.')}
            icon="trash"
            tone="danger"
            confirmLabel={this.tx('Delete role', 'حذف الدور')}
            busy={busy}
            onConfirm={this.removeRole}
            onClose={() => this.setState({ removing: null })}
          >
            <p>
              {this.tx(
                'No admin account uses this role, so nobody loses access. Deleting it cannot be undone from this dashboard.',
                'لا يستخدم أي حساب إداري هذا الدور، لذلك لن يفقد أحد الوصول. لا يمكن التراجع عن الحذف من لوحة التحكم.')}
            </p>
          </Modal>
        )}
      </AdminShell>
    );
  }
}

const mapState = state => ({ currentLocale: state && state.intl && state.intl.locale });

export default connect(mapState)(Roles);
